/**
 * Service for calculating project and media durations
 */

import { MediaItem } from '../types';

export interface Project {
  id: string;
  duration?: string;
  originalAudioUrl?: string;
  finalAudio?: string;
  // ... other project fields
}

/**
 * Get audio duration from an audio file URL
 * Returns both duration and loading state
 */
async function getAudioDuration(audioUrl: string): Promise<{ duration: string; isLoaded: boolean }> {
  try {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.addEventListener('loadedmetadata', () => {
        const duration = audio.duration;
        const minutes = Math.floor(duration / 60);
        const seconds = Math.floor(duration % 60);
        resolve({
          duration: `${minutes}:${seconds.toString().padStart(2, '0')}`,
          isLoaded: true
        });
      });
      
      audio.addEventListener('error', () => {
        resolve({ duration: '0:00', isLoaded: false }); // Fallback if audio can't be loaded
      });
      
      // Add timeout to prevent hanging
      setTimeout(() => {
        resolve({ duration: '0:00', isLoaded: false });
      }, 10000); // 10 second timeout
      
      audio.src = audioUrl;
    });
  } catch (error) {
    console.error('Error getting audio duration:', error);
    return { duration: '0:00', isLoaded: false };
  }
}

/**
 * Calculate duration for video player controls
 * Priority: 1) Audio file duration, 2) Media items timeline, 3) Default 0
 * Returns both duration and loading state
 */
export async function calculatePlayerDuration(
  project: Project | null,
  mediaItems: MediaItem[],
  videoDuration?: number
): Promise<{ duration: number; isAudioLoaded: boolean }> {
  // If there's a video file, use its duration
  if (videoDuration && videoDuration > 0) {
    return { duration: videoDuration, isAudioLoaded: true };
  }

  // Try to get duration from project's audio URL
  if (project?.originalAudioUrl || project?.finalAudio) {
    const audioUrl = project.finalAudio || project.originalAudioUrl;
    if (audioUrl) {
      try {
        const audioResult = await getAudioDuration(audioUrl);
        if (audioResult.duration !== '0:00' && audioResult.isLoaded) {
          // Convert MM:SS to seconds
          const [minutes, seconds] = audioResult.duration.split(':').map(Number);
          const totalSeconds = minutes * 60 + seconds;
          if (totalSeconds > 0) {
            return { duration: totalSeconds, isAudioLoaded: true };
          }
        }
        // If audio failed to load or returned 0:00, fall back but mark as not loaded
        if (!audioResult.isLoaded) {
          console.warn('Audio failed to load, falling back to media items');
        }
      } catch (error) {
        console.warn('Failed to get audio duration, falling back to media items:', error);
      }
    }
  }

  // Fallback to media items duration (current implementation)
  if (mediaItems.length > 0) {
    return { duration: mediaItems[mediaItems.length - 1].end, isAudioLoaded: false };
  }

  // Final fallback
  return { duration: 0, isAudioLoaded: false };
}

/**
 * Convert time string (MM:SS) to seconds
 */
export function timeStringToSeconds(timeString: string): number {
  const [minutes, seconds] = timeString.split(':').map(Number);
  return minutes * 60 + seconds;
}

/**
 * Convert seconds to time string (MM:SS)
 */
export function secondsToTimeString(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}
